import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  variantId: string;
  quantity: number;
  /** Snapshot for optimistic rendering; the server quote is always authoritative. */
  snapshot: { name: string; slug: string; image: string | null; priceGross: number; options: string };
}

interface CartState {
  items: CartItem[];
  drawerOpen: boolean;
  promoCode: string | null;
  add: (item: CartItem) => void;
  setQuantity: (variantId: string, quantity: number) => void;
  remove: (variantId: string) => void;
  clear: () => void;
  setPromoCode: (code: string | null) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  count: () => number;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      drawerOpen: false,
      promoCode: null,
      add: (item) =>
        set((state) => {
          const existing = state.items.find((i) => i.variantId === item.variantId);
          const items = existing
            ? state.items.map((i) =>
                i.variantId === item.variantId ? { ...i, quantity: Math.min(99, i.quantity + item.quantity) } : i,
              )
            : [...state.items, item];
          return { items, drawerOpen: true };
        }),
      setQuantity: (variantId, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => i.variantId !== variantId)
              : state.items.map((i) => (i.variantId === variantId ? { ...i, quantity } : i)),
        })),
      remove: (variantId) => set((state) => ({ items: state.items.filter((i) => i.variantId !== variantId) })),
      clear: () => set({ items: [], promoCode: null }),
      setPromoCode: (promoCode) => set({ promoCode }),
      openDrawer: () => set({ drawerOpen: true }),
      closeDrawer: () => set({ drawerOpen: false }),
      count: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
    }),
    { name: 'rozman.cart', partialize: (s) => ({ items: s.items, promoCode: s.promoCode }) },
  ),
);
